import React, { useState } from "react";
import { Form, Formik, Field, FieldProps, FormikHelpers } from "formik";
import { Modal } from "antd";
import * as Yup from "yup";
import * as S from "./style";
import DSButton from "../DSButton";
import { InputWrapper } from "../InputWrapper";
import { DSInput } from "../DSInput";
import { encrypt } from "../../utils/crypto";

const authorizationSchema = Yup.object({
  login: Yup.string().required("O usuário é obrigatório"),
  pwd: Yup.string().required("A senha é obrigatória"),
});

type AuthorizationSchema = Yup.InferType<typeof authorizationSchema>;

type AuthorizeChangeCardPasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isSubmitting?: boolean;
  onSubmit: (data: AuthorizationSchema) => Promise<{ success: boolean }>;
};

export function AuthorizeChangeCardPasswordModal({
  isOpen,
  onClose,
  isSubmitting = false,
  onSubmit,
}: AuthorizeChangeCardPasswordModalProps) {
  const [plainTextPassword, setPlainTextPassword] = useState("");

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<AuthorizationSchema>["setFieldValue"]
  ) => {
    const currentVisibleValue = e.target.value;
    let updatedPlainText = plainTextPassword;

    if (currentVisibleValue.length > plainTextPassword.length) {
      const addedChar = currentVisibleValue.slice(-1);
      updatedPlainText += addedChar;
    } else if (currentVisibleValue.length < plainTextPassword.length) {
      updatedPlainText = updatedPlainText.slice(0, -1);
    }

    setPlainTextPassword(updatedPlainText);

    const valueForFormik = updatedPlainText ? encrypt(updatedPlainText) : "";
    setFieldValue("pwd", valueForFormik);
  };

  const handleClose = () => {
    setPlainTextPassword("");
    onClose();
  };

  const handleSubmit = async (data: AuthorizationSchema) => {
    const { success } = await onSubmit(data);
    if (success) {
      setPlainTextPassword("");
    }
  };

  return (
    <Modal
      title="Autorização de Alteração de Senha do Cartão"
      open={isOpen}
      onCancel={handleClose}
      destroyOnClose
      closable={!isSubmitting}
      maskClosable={!isSubmitting}
    >
      <S.FormWrapper>
        <Formik
          initialValues={{ login: "", pwd: "" }}
          validationSchema={authorizationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue }) => (
            <Form>
              <Field name="login">
                {({ field, meta }: FieldProps<string>) => (
                  <InputWrapper
                    label="Usuário"
                    error={meta.touched && meta.error ? meta.error : undefined}
                    isRequired
                    name={field.name}
                  >
                    <DSInput
                      type="text"
                      placeholder="Digite o usuário"
                      {...field}
                      touched={meta.touched}
                      error={!!meta.error}
                      disabled={isSubmitting}
                      data-testid="input_usuario_autorizacao_senha_cartao"
                    />
                  </InputWrapper>
                )}
              </Field>

              <Field name="pwd">
                {({ field, meta }: FieldProps<string>) => {
                  return (
                    <InputWrapper
                      label="Senha"
                      error={
                        meta.touched && meta.error ? meta.error : undefined
                      }
                      isRequired
                      name={field.name}
                    >
                      <DSInput
                        type="text"
                        placeholder="Digite a senha"
                        {...field}
                        touched={meta.touched}
                        error={!!meta.error}
                        disabled={isSubmitting}
                        data-testid="input_senha_autorizacao_senha_cartao"
                        value={"*".repeat(plainTextPassword.length)}
                        onChange={(e) => handlePasswordChange(e, setFieldValue)}
                        autoComplete="off"
                      />
                    </InputWrapper>
                  );
                }}
              </Field>
              <S.ButtonsWrapper>
                <DSButton
                  type="reset"
                  buttonText="Cancelar"
                  buttonType="secondary"
                  isCancelType
                  onClick={handleClose}
                  height={30}
                  style={{
                    padding: "0.4rem 1.5rem",
                  }}
                  isDisabled={isSubmitting}
                  disabled={isSubmitting}
                  dataTestId="botao_cancelar_autorizacao_senha_modal"
                />
                <DSButton
                  type="submit"
                  buttonText="Confirmar"
                  buttonType="secondary"
                  height={30}
                  style={{
                    padding: "0.4rem 1.5rem",
                  }}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  disabled={isSubmitting}
                  dataTestId="botao_confirmar_autorizacao_senha_modal"
                />
              </S.ButtonsWrapper>
            </Form>
          )}
        </Formik>
      </S.FormWrapper>
    </Modal>
  );
}
